import { ActionIcon, Button, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { GeoLayer } from "shared/components/organization/geolocations/types";
import { GEO_QUERY_KEY, muts } from "shared/components/organization/geolocations/useGeoLocations";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

const ArchiveModal = ({ layer }: { layer: GeoLayer }) => {
    const {
        data: {
            geoLayer: { blockers },
        },
        refetch,
    } = useSuspenseQuery({
        queryKey: [...GEO_QUERY_KEY, "layer", layer.id, "blockers"],
        queryFn: () => httpPostGraphql(gql.blockers, { id: layer.id }),
    });

    const { mutateAsync, isPending } = useGraphqlMutation({
        document: gql.archiveGeoLayer,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: GEO_QUERY_KEY });
            notify.show.success("Layer archived");
        },
    });

    const archiveShape = useGraphqlMutation(muts.archiveShape);

    function handleArchiveShape(id: string) {
        archiveShape
            .mutateAsync({ id })
            .then(() => queryClient.invalidateQueries({ queryKey: GEO_QUERY_KEY }))
            .then(() => {
                void refetch();
                notify.show.success("Shape archived");
            })
            .catch(notify.catch);
    }

    const hasBlockers = blockers.filters.length > 0;
    return (
        <Stack gap="md">
            <Text>
                {hasBlockers ? "Layers cannot be archived until all shapes are removed." : "Are you sure you want to archive this layer?"}
            </Text>
            {hasBlockers && (
                <Stack gap="sm" mah="40dvh" style={{ overflowY: "auto" }}>
                    <Table striped stickyHeader>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Shape</Table.Th>
                                <Table.Th w={1} />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {blockers.filters.map((filter) => (
                                <Table.Tr key={filter.id}>
                                    <Table.Td>
                                        <Text fz="sm">{filter.name}</Text>
                                    </Table.Td>
                                    <Table.Td align="right">
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            onClick={() => handleArchiveShape(filter.id)}
                                            loading={archiveShape.isPending}
                                            aria-label={`Archive ${filter.name}`}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Stack>
            )}
            <Group w="100%" justify="flex-end" style={{ position: "sticky", bottom: 0 }}>
                <Button variant="default" onClick={modals.closeAll}>
                    Cancel
                </Button>
                <Button
                    disabled={blockers.isBlocked}
                    loading={isPending}
                    color="red"
                    onClick={() => void mutateAsync({ id: layer.id }).then(modals.closeAll).catch(notify.catch)}
                >
                    Archive
                </Button>
            </Group>
        </Stack>
    );
};

export function archiveGeoLayer(layer: GeoLayer) {
    modals.open({
        title: "Archive Layer",
        children: (
            <Suspense fallback={<Loader />}>
                <ArchiveModal layer={layer} />
            </Suspense>
        ),
        size: "md",
    });
}

const gql = {
    blockers: graphql(`
        query GeoLayerBlockers($id: UUID!) {
            geoLayer(id: $id) {
                blockers {
                    isBlocked
                    filters {
                        id
                        name
                    }
                }
            }
        }
    `),
    archiveGeoLayer: graphql(`
        mutation ArchiveGeoLayerWithBlockers($id: UUID!) {
            archiveGeoLayer(id: $id) {
                blockers {
                    isBlocked
                }
            }
        }
    `),
};
